import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Film, Music2, MessageSquare, ArrowUpRight } from "lucide-react";
import VideoCard from "@/components/VideoCard";
import AudioCard from "@/components/AudioCard";

export default function Dashboard() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [v, a, c] = await Promise.all([
          api.get("/videos"),
          api.get("/audios"),
          api.get("/chat/conversations"),
        ]);
        setVideos(v.data);
        setAudios(a.data);
        setConversations(c.data);
      } catch {}
    })();
  }, []);

  const stats = [
    { label: "Videos", value: videos.length, icon: Film },
    { label: "Tracks", value: audios.length, icon: Music2 },
    { label: "Chats", value: conversations.length, icon: MessageSquare },
  ];

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      <div className="reveal">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Overview</div>
          <div className="flex h-3 w-8 overflow-hidden">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tighter">
          Bonjou, {user?.name?.split(" ")[0] || "creator"}.
        </h1>
        <p className="text-[#a3a3a3] mt-3 text-sm sm:text-base max-w-xl">
          Your media, your messages, and Ayiti's living archive — all in one place.
        </p>
      </div>

      {/* Esplore Ayiti spotlight */}
      <Link
        to="/app/explore"
        data-testid="dashboard-explore-tile"
        className="mt-10 block bg-gradient-to-br from-[#141414] via-[#141414] to-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] transition-colors p-6 sm:p-8 group"
      >
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex h-3 w-8 overflow-hidden">
                <div className="flex-1 bg-[#00209F]" />
                <div className="flex-1 bg-[#D21034]" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#ff4500]">// Esplore Ayiti</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Discover Haiti's culture, history & art.</h2>
            <p className="text-sm text-[#a3a3a3] mt-2 max-w-xl">
              Dive into the 1804 Revolution, the 10 departments, soup joumou (UNESCO heritage), Carnival, kompa, rara and more.
            </p>
          </div>
          <ArrowUpRight className="w-6 h-6 text-[#a3a3a3] group-hover:text-[#ff4500] group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
        </div>
      </Link>

      <div className="grid grid-cols-3 gap-4 mt-10">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className={`bg-[#141414] border border-[#2a2a2a] p-5 reveal reveal-${i + 1}`}>
              <Icon className="w-5 h-5 text-[#ff4500]" />
              <div className="font-display text-4xl mt-4 tracking-tighter">{s.value}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3] mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Latest videos */}
      <section className="mt-14">
        <SectionHeader title="Latest videos" link="/app/videos" testid="dashboard-videos" />
        {videos.length === 0 ? (
          <EmptyState text="No videos yet. Upload your first MP4." link="/app/videos" cta="Go to videos" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.slice(0, 3).map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        )}
      </section>

      {/* Latest music */}
      <section className="mt-14">
        <SectionHeader title="Fresh tracks" link="/app/music" testid="dashboard-music" />
        {audios.length === 0 ? (
          <EmptyState text="No tracks yet. Upload your first MP3." link="/app/music" cta="Go to music" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {audios.slice(0, 5).map((a) => <AudioCard key={a.id} audio={a} queue={audios} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function SectionHeader({ title, link, testid }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{title}</h2>
      <Link to={link} data-testid={`${testid}-link`} className="text-xs uppercase tracking-[0.25em] text-[#a3a3a3] hover:text-[#ff4500] inline-flex items-center gap-1">
        See all <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function EmptyState({ text, link, cta }) {
  return (
    <div className="border border-dashed border-[#2a2a2a] p-10 text-center">
      <p className="text-sm text-[#a3a3a3]">{text}</p>
      <Link to={link} className="inline-block mt-4 px-5 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-xs uppercase tracking-[0.25em]">
        {cta}
      </Link>
    </div>
  );
}
